import React, {
    PropsWithChildren,
    ReactNode,
    MouseEventHandler,
    forwardRef
} from 'react';
import type { TButtonSize, HBButtonCustomTheme } from './HBButton.types';
import {
    Button,
    Anchor,
    CustomButton,
    IconWrapper,
    ButtonLoader,
    buttonThemes
} from './HBButton.styles';
import { vars } from '../../theme';
import { TScope, CSSDimensionValue } from '../../tokens/types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';
import { TLoaderSize } from '../HBLoader/HBLoader.styles';
import { recursivelyWrapStringChildrenWithSpans } from './HBButton.utils';

const buttonSizeToLoaderSize: Record<TButtonSize, TLoaderSize> = {
    xs: 'xs',
    sm: 'xs',
    md: 'small',
    lg: 'small',
    xl: 'small'
};

export type IButtonProps = PropsWithChildren<{
    /** Called when the button or anchor is clicked; receives the mouse event. */
    onClick?: MouseEventHandler<HTMLElement>;
    /** When provided, renders an anchor element instead of a button; disabled state removes the href to prevent navigation. */
    href?: string;
    /** Anchor target attribute (e.g. '_blank'); only applies when `href` is set. */
    target?: string;
    /** HTML id attribute applied to the root element; also satisfies the required test-ID contract. */
    id?: string;
    testId?: string;
    testIdName?: string;
    /** Semantic type tag appended to `data-testidtype`; used by test selectors that query by component type. */
    testIdType?: string;
    /** Explicit test id that wins over the internally computed one; falls back to `testId`/`testIdName`/`id` when omitted. Lets a parent that injects its own `data-testid` (e.g. a Radix `Slot` under `asChild`, as `HBMenu` does) take precedence. */
    'data-testid'?: string;
    /** Predefined visual theme; ignored when `customTheme` is provided. */
    buttonTheme?: keyof typeof buttonThemes;
    /** Overrides `buttonTheme` with inline style values and renders a CustomButton styled component instead of Button. */
    customTheme?: HBButtonCustomTheme;
    /** Visual size of the button. 'xs' for dense toolbars/tables, 'sm' for compact layouts, 'md' for standard desktop surfaces, 'lg' for mobile or large touch targets. */
    size?: TButtonSize;
    /** Design-system scope that adjusts size tokens; 'system' for internal UI, 'member' for client-facing. */
    scope?: TScope;
    /** When true, blocks all interaction and applies disabled visual styling; anchors also have their `href` removed. */
    disabled?: boolean;
    /** When true, shows a loading spinner, blocks interaction, and sets `aria-busy`. Use while an async operation is pending. */
    loading?: boolean;
    /** React icon component from `@honeybook/icons`. Use size 16 icons with `xs`/`sm`/`md` buttons; size 24 with `lg`. */
    icon?: ReactNode;
    /** Controls whether the icon renders before ('start') or after ('end') the button label. */
    iconPosition?: 'start' | 'end';
    /**
     * @deprecated - Use the `useDefaultFill` prop on individual icons from
     * `@honeybook/icons` instead. Will be removed in a future version (HBUI-326).
     */
    shouldUseIconDefaultFill?: boolean;
    /** Stretches the button to fill its parent container width. */
    fullWidth?: boolean;
    /** HTML button type; only applies when rendering as a button element (no `href`). */
    type?: 'submit' | 'button';
    /** CSS class applied to the root element. */
    className?: string;
    /** Color of the loading spinner; defaults to `vars.colors.text.primary`. */
    loaderColor?: string;
    /** Sets the tab order of the button; pass `-1` to remove it from the natural tab sequence. */
    tabIndex?: number;
    /** Outer margin applied to the button element. Accepts any CSS dimension value; defaults to `0`. */
    margin?: CSSDimensionValue;
    /** When true, displays the button in its selected/active state. */
    isSelected?: boolean;
}>;

const HBButton = forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    IButtonProps & MustIncludeOneOfBasicTestIdWithIdProps
>(function HBButton(
    {
        id,
        testIdName,
        testId,
        testIdType,
        'data-testid': injectedTestId,
        buttonTheme = 'primary',
        customTheme,
        size = 'md',
        scope = 'system',
        disabled = false,
        loading = false,
        fullWidth = false,
        icon,
        iconPosition = 'start',
        shouldUseIconDefaultFill = false,
        type = 'button',
        children,
        onClick,
        href,
        target,
        className,
        loaderColor = vars.colors.text.primary,
        tabIndex,
        margin = '0',
        isSelected = false,
        ...rest
    },
    ref
) {
    // Element swapping: use anchor if href provided, button otherwise
    const isAnchor = Boolean(href);

    const baseProps = {
        id,
        disabled: disabled || loading,
        tabIndex,
        fullWidth,
        size,
        scope,
        className,
        $isSelected: isSelected,
        'aria-pressed': isSelected || undefined,
        'aria-busy': loading || undefined
    };

    // Add element-specific props
    const elementSpecificProps = isAnchor
        ? {
              href: disabled ? undefined : href,
              target,
              onClick
          }
        : { type, onClick };

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id,
        suffix: 'button',
        componentName: 'HBButton',
        testIdType,
        enforce: false
    });

    const wrappedChildren = recursivelyWrapStringChildrenWithSpans(children);
    const loaderSize = buttonSizeToLoaderSize[size];

    // Common props for both button and anchor
    const commonProps = {
        ...baseProps,
        'data-testid': injectedTestId ?? finalTestId,
        'data-testidtype': testIdType,
        theme: buttonTheme,
        customTheme,
        $margin: margin,
        $shouldUseIconDefaultFill: shouldUseIconDefaultFill
    };

    const content = (
        <>
            {loading && (
                <ButtonLoader
                    testId="button--loader"
                    size={loaderSize}
                    color={loaderColor}
                />
            )}
            {icon && (
                <IconWrapper iconPosition={iconPosition} size={size}>
                    {icon}
                </IconWrapper>
            )}
            {wrappedChildren}
        </>
    );

    if (isAnchor) {
        return (
            <Anchor
                ref={ref as React.Ref<HTMLAnchorElement>}
                {...rest}
                {...commonProps}
                {...elementSpecificProps}
            >
                {content}
            </Anchor>
        );
    }

    const ButtonComp = customTheme ? CustomButton : Button;
    return (
        <ButtonComp
            ref={ref as React.Ref<HTMLButtonElement>}
            {...rest}
            {...commonProps}
            {...elementSpecificProps}
        >
            {content}
        </ButtonComp>
    );
});

export default HBButton;
